import React, { useEffect, useState } from 'react';
import CenterStage from '../Components/CenterStage/CenterStage';
import SectionHeader from '../Components/SectionHeader/SectionHeader';
import { useHistory } from 'react-router-dom';
import { FaCheckCircle, FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaArrowLeft, FaPaperPlane } from 'react-icons/fa';
import imgHANSAFLEX_Ireland_Thank_you from '../assets/Thank_you.webp';
import './ThankYouPage.css';

const ThankYouPage = () => {
    const history = useHistory();
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        // Trigger animation after component mounts
        setTimeout(() => setIsVisible(true), 100);
    }, []);

    const handleBackToHome = () => {
        history.push('/');
    };

    const handleBackToContact = () => {
        history.push('/contact-us');
    };

    const handleContactClick = (type, value) => {
        switch (type) {
            case 'phone':
                window.open(`tel:${value}`, '_self');
                break;
            case 'email':
                window.open(`mailto:${value}`, '_self');
                break;
            case 'address':
                // Open Google Maps with the address
                const encodedAddress = encodeURIComponent(value);
                window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
                break;
            default:
                break;
        }
    };

    const contactInfo = [
        {
            icon: <FaPhone />,
            title: "Northern Ireland",
            value: "+44 028 90833813",
            type: "phone"
        },
        {
            icon: <FaPhone />,
            title: "Republic of Ireland", 
            value: "+353 01 6251962",
            type: "phone"
        },
        {
            icon: <FaEnvelope />,
            title: "Email",
            value: "ni.info@hansa-flex.com",
            type: "email"
        },
        {
            icon: <FaMapMarkerAlt />,
            title: "Address",
            value: "74 Derriaghy Industrial Park, BT17 9HU Dunmurry, Co. Antrim",
            type: "address"
        },
        {
            icon: <FaClock />,
            title: "Business Hours",
            value: "Monday - Friday: 8:00 AM - 5:00 PM",
            type: "hours"
        }
    ];

    return (
        <div className="thank-you-page">
            <div>
                <CenterStage
                    imageSrc={imgHANSAFLEX_Ireland_Thank_you}
                    title={"Thank You for Contacting Us!"}
                    paragraph={"Your message has been sent successfully. Our team will get back to you as soon as possible. We appreciate your interest in HANSA-FLEX Ireland."} />
            </div>
            
            <div className="container mt-5 mb-5">
                {/* Success Animation Section */}
                <div className={`success-animation-section ${isVisible ? 'animate-in' : ''}`}>
                    <div className="row justify-content-center">
                        <div className="col-md-8 text-center">
                            <div className="success-circle">
                                <FaCheckCircle className="success-icon" />
                            </div>
                            <h2 className="success-title">Message Sent Successfully!</h2>
                            <p className="success-subtitle">
                                We have received your inquiry and our team will review it shortly. 
                                You can expect a response within <strong>24-48 hours</strong> during business days.
                            </p>
                        </div>
                    </div>
                </div>

                {/* What Happens Next Section */}
                <div className={`next-steps-section ${isVisible ? 'animate-in' : ''}`}>
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <SectionHeader 
                                title="What Happens Next?" 
                                paragraph="Here's what you can expect from our team" 
                            />
                            
                            <div className="row mt-4 mb-4">
                                <div className="col-md-4">
                                    <div className="step-card">
                                        <div className="step-number">1</div>
                                        <h4>Review & Analysis</h4>
                                        <p>Our experts will carefully review your inquiry and gather all necessary information.</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="step-card">
                                        <div className="step-number">2</div>
                                        <h4>Expert Response</h4>
                                        <p>You'll receive a detailed response from our specialized team within 24-48 hours.</p>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="step-card">
                                        <div className="step-number">3</div>
                                        <h4>Ongoing Support</h4>
                                        <p>We'll continue to support you throughout your project with our expertise.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Contact Information Section */}
                <div className={`contact-info-section ${isVisible ? 'animate-in' : ''}`}>
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="contact-info-card">
                                <SectionHeader 
                                    title="Need Immediate Assistance?" 
                                    paragraph="If you have any urgent questions, please don't hesitate to contact us directly:" 
                                />
                                
                                <div className="row mt-4">
                                    {contactInfo.map((info, index) => (
                                        <div key={index} className="col-md-6 col-lg-4 mb-3">
                                            <div 
                                                className={`contact-item ${info.type !== 'hours' ? 'clickable' : ''}`}
                                                onClick={() => info.type !== 'hours' && handleContactClick(info.type, info.value)}
                                                style={{ cursor: info.type !== 'hours' ? 'pointer' : 'default' }}
                                            >
                                                <div className="contact-icon">
                                                    {info.icon}
                                                </div>
                                                <div className="contact-details">
                                                    <h6>{info.title}</h6>
                                                    <p>{info.value}</p>
                                                    {info.type !== 'hours' && (
                                                        <small className="click-hint">Click to {info.type === 'phone' ? 'call' : info.type === 'email' ? 'email' : 'view on map'}</small>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons Section */}
                <div className={`action-buttons-section ${isVisible ? 'animate-in' : ''}`}>
                    <div className="row justify-content-center">
                        <div className="col-md-8 text-center">
                            <div className="action-buttons">
                                <button 
                                    className="btn btn-primary btn-lg me-3"
                                    onClick={handleBackToHome}
                                >
                                    <FaArrowLeft className="me-2" />
                                    Back to Home
                                </button>
                                <button 
                                    className="btn btn-outline-primary btn-lg"
                                    onClick={handleBackToContact}
                                >
                                    <FaPaperPlane className="me-2" />
                                    Contact Us Again
                                </button>
                            </div>
                        </div>
                    </div>
                </div>


            </div>
        </div>
    );
};

export default ThankYouPage;
